import { BaseResponse } from './api';

export enum GeoJsonType {
  Point = 'Point'
}

export interface GeoJsonGeometry {
  type: GeoJsonType;
  coordinates: [number, number];
}

export interface AddressResponseV1 extends BaseResponse {
  addressId: string;
  userProfileId: string;
  name: string;
  deliveryNotes?: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  isDefault: boolean;
  geometry: GeoJsonGeometry;
  // IANA timezone of the address (e.g. "America/Denver"), supplied by the backend. Used to evaluate
  // local-time cutoffs such as the Schedule & Save → on-demand switch. Optional until every address is
  // backfilled; callers fall back to the device timezone when it is absent.
  ianaZone?: string;
}

export interface Address {
  name: string;
  deliveryNotes?: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isDefault: boolean;
  geometry: GeoJsonGeometry;
}

export type GeoAddress = Omit<Address, 'name' | 'deliveryNotes' | 'isDefault'>;

export interface AddressForm {
  name: string;
  deliveryNotes: string | null;
  street: string;
  street2: string | null;
  city: string;
  state: string | null;
  zipCode: string;
  country: string | null;
  isDefault: boolean;
  latitude: number;
  longitude: number;
}

export interface AddressValidationResult {
  needsFix: boolean;
  hasCorrection: boolean;
  validatedAddress: AddressResponseV1;
  formattedEnteredAddress: string;
  formattedValidatedAddress: string;
}

export interface AddressValidationModalOptions {
  enteredAddress: string;
  validatedAddress: string;
  needsFix: boolean;
}

export enum AddressValidationModalResult {
  UseEntered = 'use-entered',
  UseValidated = 'use-validated',
  Cancel = 'cancel'
}

export enum GoogleAddressAction {
  Accept = 'ACCEPT',
  Fix = 'FIX',
  Confirm = 'CONFIRM',
  Unspecified = 'UNSPECIFIED'
}

export interface GoogleAddressValidationResponse {
  result: {
    verdict: {
      hasUnconfirmedComponents?: boolean;
      hasInferredComponents?: boolean;
      hasReplacedComponents?: boolean;
      hasSpellCorrectedComponents?: boolean;
      possibleNextAction?: GoogleAddressAction;
    };
    address: {
      formattedAddress: string;
      postalAddress: {
        regionCode: string;
        administrativeArea: string;
        locality: string;
        postalCode: string;
        addressLines: string[];
      };
    };
    geocode: {
      location: {
        latitude: number;
        longitude: number;
      };
    };
  };
}

export interface AddressValidationResponse {
  needsFix: boolean;
  hasCorrection: boolean;
  validatedAddress: GeoAddress;
  formattedEnteredAddress: string;
  formattedValidatedAddress: string;
}

// A prediction is the cheap half of the flow: it carries only what the dropdown needs to show
// (text) and what we need to resolve the pick (placeId). Place Details are fetched lazily, for the
// single prediction the user selects, so a query no longer costs one Place Details call per row.
export interface AutocompletePrediction {
  placeId: string;
  text: string;
}
